/**
 * Modelos de mensagem aprovados — a parte PURA (sem banco, sem servidor),
 * para a tela e o servidor falarem a mesma língua.
 *
 * Na oficial da Meta eles precisam ser cadastrados e aprovados antes de
 * existir; aqui ficam a lista e a prévia que a pessoa confere antes de
 * enviar. Isto NÃO fala com a Meta: um nome cadastrado aqui só é testado de
 * verdade na hora do envio real.
 */

/** "ficha" é o que impede uma campanha de mandar "limpeza de pele" para quem quer botox. */
export type FonteVariavel = "nome" | "interesse" | "manual";

export interface Variavel {
  rotulo: string;
  fonte: FonteVariavel;
}

export type CategoriaModelo = "MARKETING" | "UTILIDADE" | "AUTENTICACAO";

export interface ModeloMensagem {
  nome: string;
  categoria: CategoriaModelo;
  corpo: string;
  /** Cada {{n}}, na ordem. */
  variaveis: Variavel[];
}

export const CATEGORIAS: Array<{ id: CategoriaModelo; rotulo: string; descricao: string }> = [
  { id: "UTILIDADE", rotulo: "Utilidade", descricao: "lembrete, confirmação, aviso sobre a consulta" },
  { id: "MARKETING", rotulo: "Marketing", descricao: "novidade, promoção, reativação de cliente" },
  { id: "AUTENTICACAO", rotulo: "Autenticação", descricao: "envio de código ou confirmação de segurança" },
];

export const MODELOS_PADRAO: ModeloMensagem[] = [
  {
    nome: "lembrete_consulta",
    categoria: "UTILIDADE",
    corpo: "Olá {{1}}, tudo bem? Passando para lembrar da sua consulta.\n\nServiço: {{2}}\nQuando: {{3}}\n\nSe precisar remarcar, é só responder por aqui.",
    variaveis: [
      { rotulo: "Nome do cliente", fonte: "nome" },
      { rotulo: "Serviço", fonte: "interesse" },
      { rotulo: "Data e hora", fonte: "manual" },
    ],
  },
  {
    nome: "retomar_conversa",
    categoria: "UTILIDADE",
    corpo: "Oi {{1}}! Vi que a gente ficou de falar sobre {{2}} e a conversa acabou parando por aqui. Quer que eu veja um horário pra você?",
    variaveis: [
      { rotulo: "Nome do cliente", fonte: "nome" },
      { rotulo: "Serviço de interesse", fonte: "interesse" },
    ],
  },
  {
    nome: "promocao_servico",
    categoria: "MARKETING",
    corpo: "Oi {{1}}! Temos novidade em {{2}}: {{3}}. Quer saber mais?",
    variaveis: [
      { rotulo: "Nome do cliente", fonte: "nome" },
      { rotulo: "Serviço", fonte: "interesse" },
      { rotulo: "A condição", fonte: "manual" },
    ],
  },
];

/** Troca {{1}}, {{2}}… pelos valores. Vazio vira o marcador, para ficar visível. */
export function preencher(corpo: string, variaveis: string[]): string {
  return corpo.replace(/\{\{(\d+)\}\}/g, (marca, n) => variaveis[Number(n) - 1]?.trim() || marca);
}

/** Monta as variáveis de UMA pessoa: o que vem da ficha vem da ficha dela. */
export function variaveisPara(modelo: ModeloMensagem, pessoa: { nome?: string | null; interesse?: string | null }, manuais: string[]): string[] {
  return modelo.variaveis.map((v, i) => {
    if (v.fonte === "nome") return pessoa.nome ?? "";
    if (v.fonte === "interesse") return pessoa.interesse ?? "";
    return manuais[i] ?? "";
  });
}

export function validarModelo(m: ModeloMensagem): string | null {
  if (!m.nome.trim()) return "Falta o nome do modelo.";
  if (!/^[a-z0-9_]+$/.test(m.nome)) return 'O nome só pode ter letras minúsculas, números e "_" — é o que a Meta exige.';
  if (!m.corpo.trim()) return "Falta o texto do modelo.";
  const usadas = new Set([...m.corpo.matchAll(/\{\{(\d+)\}\}/g)].map((x) => Number(x[1])));
  for (let i = 1; i <= m.variaveis.length; i++) {
    if (!usadas.has(i)) return `A variável {{${i}}} está declarada, mas não aparece no texto.`;
  }
  const maior = Math.max(0, ...usadas);
  if (maior > m.variaveis.length) return `O texto usa {{${maior}}}, mas só ${m.variaveis.length} variável(is) foram declaradas.`;
  return null;
}
